<script>
import { getToolsClient } from '@/services/tools'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import LineChart from '@/components/Chart/Line'
import * as deviceTools from '@/services/rest-api-tools/lib/device/device/'
import * as rawSensorInfo from '@/services/rest-api-sms/lib/raw-sensor-info/raw-sensor-info/'
import * as vehicleApi from '@/services/rest-api-sms/lib/vehicle/vehicle'

const toolsAxios = getToolsClient()

export default {
  props: {
    region: {
      type: String,
      default: 'SG',
    },
  },

  components: { LineChart },

  mixins: [Helper],

  data() {
    return {
      isTable: true,
      graphData: {
        datasets: [],
        labels: [],
      },
      dateRange: [],
      rawFuelData: null,
      isMounted: false,
      initialDeviceData: null,
      imei: null,
      vehicleID: null,
      isLoading: true,
      maxDate: 1,
      pastDate: null,
      futureEnd: moment().add(24, 'hours'),
      isTimeChanged: false,
      fuelRaws: [],
      filteredItems: [],
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      totalRows: 1,
      currentPage: 1,
      loading: false,
      filter: null,
      filterOn: [],
    }
  },

  watch: {
    async isTable(newVal) {
      if (newVal && this.graphData.datasets[0].data?.length > 0) {
        await this.getFuelRaw()
        return
      }
      await this.getFuelRawWithoutLimit()
      this.getGraphData()
    },

    region() {
      this.reloadModule()
    },
  },

  computed: {
    fuelRawFields() {
      return [
        {
          label: 'Created On',
          key: 'created_on_formatted',
          sortable: true,
          type: 'date',
          can_edit: false,
          can_delete: false,
        },
        {
          label: 'Fuel Filtered MEP (%)',
          key: 'fuel',
          sortable: true,
          type: 'text',
          can_edit: false,
          can_delete: false,
        },
        {
          label: 'Fuel Raw',
          key: 'fuel_raw',
          sortable: true,
          type: 'text',
          can_edit: false,
          can_delete: false,
        },
      ]
    },

    datasets() {
      return [
        {
          label: 'Fuel Filtered MEP (%)',
          fill: false,
          lineTension: 0.5,
          backgroundColor: 'transparent',
          borderColor: '#0d4491',
          borderCapStyle: 'butt',
          borderDash: [],
          borderDashOffset: 0.0,
          borderJoinStyle: 'miter',
          pointBorderColor: '#0d4491',
          pointBackgroundColor: '#fff',
          pointBorderWidth: 1,
          pointHoverRadius: 7,
          pointHoverBackgroundColor: '#0d4491',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          pointRadius: 1,
          pointHitRadius: 10,
        },
        {
          label: 'Fuel Raw',
          fill: false,
          lineTension: 0.5,
          backgroundColor: 'transparent',
          borderColor: '#F41D00',
          borderCapStyle: 'butt',
          borderDash: [],
          borderDashOffset: 0.0,
          borderJoinStyle: 'miter',
          pointBorderColor: '#F41D00',
          pointBackgroundColor: '#fff',
          pointBorderWidth: 1,
          pointHoverRadius: 7,
          pointHoverBackgroundColor: '#F41D00',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          pointRadius: 1,
          pointHitRadius: 10,
        },
      ]
    },

    shortcuts() {
      let startDay = new Date()
      startDay.setHours(0, 0, 0)

      let endDay = new Date()
      endDay.setHours(23, 59, 59)
      return [{ text: 'Hari Ini', onClick: () => [startDay, endDay] }]
    },
  },

  async created() {
    this.initialDeviceData = JSON.parse(localStorage.getItem('device'))
    this.imei = this.initialDeviceData?.imei

    if (this.imei) {
      await this.getVehicleIdFromIMEI()
    }

    await this.getFuelRaw()
  },

  methods: {
    async changePage(newPage) {
      this.currentPage = newPage
      await this.getFuelRaw()
    },

    resetFilter(e) {
      this.currentPage = 1
      this.perPage = e
      this.getFuelRaw()
    },

    async getFuelRaw() {
      this.isLoading = true
      this.loading = true
      if (!this.vehicleID || this.dateRange[0] === null) {
        this.fuelRaws = []
        this.filteredItems = []
        this.totalRows = 0
        return
      }

      const dateRange = [
        this.momentYmDHmsPayload(this.dateRange[0], true),
        this.momentYmDHmsPayload(this.dateRange[1], true),
      ]

      let params = {
        page: this.currentPage,
        limit: this.perPage, // Changed from size to limit
        filters: [
          `created_on gte '${dateRange[0]}'`,
          `created_on lte '${dateRange[1]}'`,
          `vehicle_id eq ${this.vehicleID}`,
        ],
      }

      const rawSensorInfos = await rawSensorInfo.get(params, this.region)

      rawSensorInfos.data.paginated_result.forEach((data) => {
        data.created_on_formatted = this.momentDMHms(new Date(data.created_on))
        data.fuel = data.fuel ? data.fuel : 0
      })

      this.fuelRaws = rawSensorInfos.data.paginated_result
      this.filteredItems = rawSensorInfos.data.paginated_result
      this.totalRows = rawSensorInfos.metadata.total_count
      this.loading = false
      this.isLoading = false
    },

    async getFuelRawWithoutLimit() {
      this.isLoading = true
      this.loading = true
      if (!this.vehicleID || this.dateRange[0] === null) {
        this.fuelRaws = []
        this.filteredItems = []
        this.totalRows = 0
        return
      }

      const dateRange = [
        this.momentYmDHmsPayload(this.dateRange[0], true),
        this.momentYmDHmsPayload(this.dateRange[1], true),
      ]

      let params = {
        page: 1,
        limit: 1, // Changed from size to limit
        filters: [
          `created_on gte '${dateRange[0]}'`,
          `created_on lte '${dateRange[1]}'`,
          `vehicle_id eq ${this.vehicleID}`,
        ],
      }

      let rawSensorInfos = await rawSensorInfo.get(params, this.region)

      params.limit = rawSensorInfos.metadata.total_count
      rawSensorInfos = await rawSensorInfo.get(params, this.region)

      rawSensorInfos.data.paginated_result.forEach((data) => {
        data.created_on_formatted = this.momentDMHms(new Date(data.created_on))
        data.fuel = data.fuel ? data.fuel : 0
      })

      this.fuelRaws = rawSensorInfos.data.paginated_result
      this.filteredItems = rawSensorInfos.data.paginated_result
      this.totalRows = rawSensorInfos.metadata.total_count
      this.loading = false
      this.isLoading = false
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length
      this.filteredItems = filteredItems
      this.currentPage = 1
    },

    getGraphData() {
      const items = this.filteredItems
      const datasets = []
      const labels = []
      items.sort((a, b) => new Date(a.created_on) - new Date(b.created_on))
      datasets[0] = {
        ...this.datasets[0],
        data: items?.map((e) => e.fuel),
      }

      datasets[1] = {
        ...this.datasets[1],
        data: items?.map((e) => e.fuel_raw),
      }

      items?.forEach((data) => {
        const createdDate = this.momentYmDHms(new Date(data.created_on))
        labels.push(createdDate)
        data.created_on = this.momentDMHms(new Date(data.created_on))
      })
      this.graphData.datasets = datasets
      this.graphData.labels = labels
    },

    async getVehicles(_params) {
      let params = {
        page: 1,
        limit: this.size,
        search: this.search,
        filters: ["imei != ''", "imei != ' '", 'imei is not null'],
      }

      if (_params) {
        params.page = _params.page
        params.limit = _params.limit
        params.search = _params.search
      }
      const vehicles = await vehicleApi.get(params, this.region)

      if (!vehicles?.data) return
      return vehicles
    },

    async getVehicleIdFromIMEI() {
      const device = await deviceTools.get(this.imei, this.region)
      this.vehicleID = device.data?.vehicleId
    },

    pickDate(date) {
      let dateRange = this.dateRange
      if (dateRange[0] === null && dateRange[1] === null) {
        dateRange[0] = this.momentYmDHms(date)
      }
      if (dateRange[1] === null) dateRange[1] = this.momentYmDHms(date)

      this.pastDate = new Date(date.getTime() - 24 * this.maxDate * 3600 * 1000)
      if (new Date(date.getTime() + 24 * 0 * 3600 * 1000) == new Date()) {
        this.futureEnd = new Date(date.getTime() + 24 * 0 * 3600 * 1000)
      } else {
        this.futureEnd = new Date(date.getTime() + 24 * this.maxDate * 3600 * 1000)
      }
    },

    disabledDate: function (date) {
      let today = new Date()
      return date > this.futureEnd || date > today || (this.pastDate && date <= this.pastDate)
    },

    disabledTime: function (time) {
      if (this.dateRange.length === 0) return false
      let startRange = new Date(this.dateRange[0])
      const nextTimeDisabled = new Date(startRange.getTime() + 24 * 3600 * 1000)
      return time > nextTimeDisabled
    },

    handleOpenDatePicker(date) {
      if (date[0] === null && date[1] === null) {
        this.pastDate = null
        this.futureEnd = moment().add(24, 'hours')
      }

      this.getFuelRaw()
    },

    async onChangeImei() {
      await this.getVehicleIdFromIMEI()
      await this.getFuelRaw()
    },

    async getDeviceAfterRegionChanged() {
      if (!this.imei) return
      const res = await toolsAxios.get(`/device?page=1&size=1`, {
        headers: {
          'X-Region': this.region,
        },
      })

      this.imei = res.data.data[0].imei
      this.initialDeviceData = res.data.data[0]
      this.imei = this.initialDeviceData?.imei
    },

    async reloadModule() {
      await this.getDeviceAfterRegionChanged()
      await this.getVehicles()
      await this.getVehicleIdFromIMEI()
      await this.getFuelRaw()
    },
  },
}
</script>

<style>
input.mx-input {
  font-size: small;
}
</style>

<template>
  <div>
    <label class="align-self-center px-3 fw-bold">Period</label>
    <div class="d-flex px-3 col-8">
      <date-picker
        v-model="dateRange"
        type="datetime"
        :placeholder="'Pilih Tanggal'"
        range
        :shortcuts="shortcuts"
        value-type="YYYY-MM-DD HH:mm:ss"
        format="YYYY-MM-DD HH:mm:ss"
        id="mc-datepicker-fuel"
        @pick="pickDate"
        :disabled-date="disabledDate"
        :disabled-time="disabledTime"
        @input="handleOpenDatePicker"
      />
      <FormulateInput
        type="select-resourceful"
        v-model="imei"
        placeholder="Pilih IMEI"
        :optionsGetter="getVehicles"
        :optionLabelFn="
          ({ item }) => {
            return item.imei
          }
        "
        class="pt-0 col-5"
        trackBy="imei"
        :allow-empty="false"
        :initialObject="initialDeviceData"
        @input="onChangeImei"
      />
    </div>
    <div class="col-md-6 p-3">
      <b-button-group class="mb-3">
        <b-button @click="isTable = true" :variant="isTable ? 'primary' : 'outline-primary'">
          <i class="mc-fa icon-outline_browser" /> Table
        </b-button>
        <b-button @click="isTable = false" :variant="isTable ? 'outline-primary' : 'primary'">
          <i class="mc-fa icon-fill_activity" /> Grafik
        </b-button>
      </b-button-group>
    </div>

    <div v-if="isTable">
      <div class="table-responsive my-3 border border-1">
        <b-table
          class="datatables"
          :items="fuelRaws"
          ref="fuelRawTableRef"
          :fields="fuelRawFields"
          show-empty
          :empty-text="$t('global.emptyTable')"
          :empty-filtered-text="$t('global.emptyFilteredTable')"
          :per-page="perPage"
          :filter="filter"
          :filter-included-fields="filterOn"
          @filtered="onFiltered"
          :busy="isLoading"
          :no-local-sorting="true"
          empty-table
        >
          <template #cell(time)="data">
            {{ momentDMHms(data.value) }}
          </template>

          <template #cell(company)="data">
            {{ `${data.item.company.companyName} (${data.item.company.domainName})` }}
          </template>
          <template #cell(detail)="data">
            <span v-html="formatDetailWithLocalTime(data.value)"></span>
          </template>
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>
        </b-table>
      </div>

      <div class="row">
        <div class="col-sm-12 col-md-6">
          <div id="tickets-table_length" class="dataTables_length" v-if="!loading">
            <label class="d-inline-flex align-items-center">
              <span>Menampilkan</span>
              <span>
                <b-form-select
                  class="form-select form-select-sm"
                  v-model="perPage"
                  size="sm"
                  :options="pageOptions"
                  @change="resetFilter"
                />
              </span>
              <span>entri.&nbsp;&nbsp;</span>
              <span
                >Menampilkan&nbsp;
                <strong>
                  {{ (filteredItems?.length || 0) > 0 ? (currentPage - 1) * perPage + 1 : 0 }}
                </strong>
                &nbsp;sampai&nbsp;
                <strong>
                  {{ Math.min(totalRows, currentPage * perPage) }}
                </strong>
                &nbsp;dari&nbsp;
                <strong>{{ totalRows }}</strong>
                &nbsp;entri.
              </span>
            </label>
          </div>
        </div>
        <div class="col-sm-12 col-md-6">
          <div class="dataTables_paginate paging_simple_numbers float-end">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination
                v-if="!loading"
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                @change="changePage"
              ></b-pagination>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <LineChart
      v-if="!isTable"
      :labels="graphData.labels || []"
      :datasets="graphData.datasets || []"
    />
  </div>
</template>
